"use client";

import { readJson } from "@/app/_utils/apiJson";

import {
  ActionIcon,
  Portal,
  Stack,
  Title,
  NumberInput,
  TextInput,
  Select,
  Alert,
  Group,
  Text,
  Divider,
  Button,
  Anchor,
  Tabs,
  Loader,
  Paper,
  Switch,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCurrencyDollar,
  IconCreditCard,
  IconCash,
} from "@tabler/icons-react";
import { useState, useRef, useEffect, useMemo } from "react";
import {
  useStripeTerminalContext,
  useReaderLocked,
  useHoldReaderLock,
  ReaderAcquireFailure,
  readerFailureMessage,
} from "@/app/_providers/StripeTerminalProvider";
import {
  useTerminalPayment,
  describeTerminalChargeError,
} from "@/app/_hooks/useTerminalPayment";
import PaymentModal from "@/app/_components/Payment/PaymentModal/PaymentModal";
import PaymentNotice from "@/app/_components/Payment/PaymentNotice/PaymentNotice";
import { ReaderSummary } from "@/app/_components/ReaderPicker/ReaderSummary";
import { ReaderFailureNotice } from "@/app/_components/ReaderPicker/ReaderFailureNotice";
import ReaderPicker from "@/app/_components/ReaderPicker/ReaderPicker";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import { useUserContext } from "@/app/_hooks/useUserContext";
import {
  calculateJob,
  calculateAddition,
  isGreaterThan,
  nearZero,
  toDecimal,
} from "@/app/_helpers/math";
import useGetJobsWithCacheV2 from "@/app/_hooks/useGetJobsWithCacheV2";
import { useQuery } from "@tanstack/react-query";
import { useGetJobByJobUid } from "@/app/_hooks/jobs/useGetJobByJobUid";
import { getJobsIndex, jobsIndexQueryKey } from "@/app/_firebase/jobsIndex";
import { isJobsIndexEnabledFor } from "@/app/_constants/jobsIndex";
import TakeJobPaymentForm from "@/app/_components/Payment/TakeJobPaymentForm/TakeJobPaymentForm";
import { getJobByQbInvoiceId } from "@/app/_firebase/jobs";
import { createOneOffPayment } from "@/app/_firebase/oneOffPayments";
import { authenticatedFetch } from "@/app/_utils/authenticatedFetch";
import useFormatCurrency from "@/app/_hooks/useFormatCurrency";
import { getCurrencyDetailsByCode } from "@/app/_helpers/country";
import { Client } from "@/app/@types/client";
import { NamedUserUID, PaymentStatus, PaymentType } from "@/app/@types/enums";
import { JobPayment, JobV2 } from "@/app/@types/job";
import { OneOffPayment } from "@/app/@types/oneOffPayment";
import { Timestamp } from "firebase/firestore";
import { getPlatformStripePromise, loadStripe } from "@/app/_utils/stripeClient";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";


// Where the button was last dragged to; survives refresh so it stays out of
// the way once the user parks it somewhere (same as the timer pill).
const POSITION_KEY = "posTrigger:position";

// Non-card methods the One-off tab can record. Same list as the job payment
// form, minus the card tenders.
const RECORD_TENDERS = [
  "Cash",
  "Check",
  "Bank Transfer",
  "Venmo",
  "PayPal",
  "Other",
];

const clampToViewport = (pos: { x: number; y: number }) => ({
  x: Math.min(Math.max(pos.x, 8), window.innerWidth - 72),
  y: Math.min(Math.max(pos.y, 8), window.innerHeight - 72),
});

function POSTriggerContent() {
  const [opened, { open, close }] = useDisclosure(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  // Latest position, readable from the drag-end listeners (which are bound
  // once and would otherwise close over a stale value).
  const positionRef = useRef(position);
  positionRef.current = position;
  const dragRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const hasMovedRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const addAlert = useSetAlert();
  const user = useUserContext();
  const formatCurrency = useFormatCurrency();
  const { readers, hasMultipleReaders, mustPickReader } =
    useStripeTerminalContext();
  // Any payment on this device (either tab) is under way or being saved.
  const readerLocked = useReaderLocked();
  // Nothing holds the reader between payments. Opening the modal does not
  // touch it; the one-off Charge takes it through runWithReader, charges,
  // and lets it go, the same as the job payment form.
  const {
    runWithReader,
    cancelTerminalPayment,
    isProcessing: isReaderProcessing,
    canCancel,
    isTerminalReady,
  } = useTerminalPayment();
  // The reader is the way to charge here: one resolves, or the picker is
  // waiting for this device to choose one.
  const readerAvailable = isTerminalReady || mustPickReader;

  useEffect(() => {
    // Clear the On Job selection so the next open starts fresh.
    if (!opened) setOnJobSelectedUid(null);
  }, [opened]);

  const userTaxRate = user?.settings?.taxRate || 0;

  // Each open starts from the account default: tax included whenever a tax
  // rate is configured, since one-off charge amounts are tax-inclusive the
  // same way job balances are.
  useEffect(() => {
    if (opened) setOneOffTaxIncluded(userTaxRate > 0);
  }, [opened, userTaxRate]);

  const userCurrency = useMemo(() => {
    return getCurrencyDetailsByCode(user?.settings?.currency || "USD");
  }, [user?.settings?.currency]);

  // One-off charge state
  const [oneOffAmount, setOneOffAmount] = useState<number>(1);
  const [oneOffDescription, setOneOffDescription] = useState("");
  // Whether the one-off amount includes sales tax at the account's rate.
  // Recorded on the payment so the payments report can extract the tax
  // portion; the charge amount itself is never changed by this.
  const [oneOffTaxIncluded, setOneOffTaxIncluded] = useState(false);
  const [selectedProcessingFee, setSelectedProcessingFee] = useState<
    string | null
  >(null);
  const [isProcessingOneOff, setIsProcessingOneOff] = useState(false);
  const hasAutoSelectedFee = useRef(false);
  const [qbInvoiceId, setQbInvoiceId] = useState("");
  const [qbInvoiceLookup, setQbInvoiceLookup] = useState<{
    status: "idle" | "loading" | "found" | "notFound";
    invoice?: any;
  }>({ status: "idle" });
  // Set when the looked-up invoice belongs to a MountMonitor job — one-off
  // payments on those would desync MM from QuickBooks, so we block them and
  // steer the user to the On Job tab with that job preselected.
  const [linkedMmJob, setLinkedMmJob] = useState<{
    jobId: number;
    uid: string;
  } | null>(null);
  // Selection for the On Job tab — lifted here so the one-off guard can
  // preselect the owning job when steering the user across.
  const [onJobSelectedUid, setOnJobSelectedUid] = useState<string | null>(null);
  // Card Reader when a reader can be reached for, otherwise Manual Entry so
  // a card can still be typed in. Re-evaluated on each open and when the
  // reader list arrives, unless the user has clicked a tab themselves.
  const [activeTab, setActiveTab] = useState<string | null>(
    readerAvailable && readers.length > 0 ? "card-reader" : "manual-entry"
  );
  const userPickedTab = useRef(false);
  // Cash, check, Venmo etc. are recorded, not charged: no Stripe, no
  // processing fee.
  const isRecordOnly = activeTab === "record";
  const [recordTender, setRecordTender] = useState<string>("Cash");
  const [isRecordingOneOff, setIsRecordingOneOff] = useState(false);
  useEffect(() => {
    if (!opened) {
      userPickedTab.current = false;
      return;
    }
    if (userPickedTab.current) return;
    // Never land on the Card Reader tab while it is disabled (no readers).
    setActiveTab(
      readerAvailable && readers.length > 0 ? "card-reader" : "manual-entry"
    );
  }, [opened, readerAvailable, readers.length]);
  // The Card Reader tab is offered whenever the shop has a reader registered,
  // even if none resolves for this device yet (the picker sorts that out
  // inside the tab).
  const hasAnyReader = readers.length > 0;
  // A reader-path failure shown inline on the Card Reader tab. readerFailure
  // is set when the reader itself could not be taken (busy, offline, gone)
  // rather than the card failing.
  const [readerAlert, setReaderAlert] = useState<{
    message: string;
    readerFailure?: ReaderAcquireFailure;
    cardDeclined?: boolean;
  } | null>(null);
  // "Change reader" opens the picker over the current choice. Only shops
  // with more than one reader ever see it.
  const [showReaderPicker, setShowReaderPicker] = useState(false);
  useEffect(() => {
    if (opened) return;
    setReaderAlert(null);
    setShowReaderPicker(false);
  }, [opened]);
  const pickerVisible =
    mustPickReader || (hasMultipleReaders && showReaderPicker);
  // The one-off charge and its QuickBooks/record steps keep the modal shut
  // until they finish, like the job payment's save.
  useHoldReaderLock(isProcessingOneOff);
  // Top-level mode: take a payment against a job, or a one-off charge.
  const [posMode, setPosMode] = useState<string | null>("job");

  // The QB internal transaction id used when charging — the lookup resolves
  // it even when the user typed the printed invoice number instead.
  const resolvedQbInvoiceId =
    qbInvoiceLookup.status === "found"
      ? String(qbInvoiceLookup.invoice.Id || qbInvoiceId)
      : qbInvoiceId;

  // Manual payment state - these hooks need to be inside Elements provider
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessingManual, setIsProcessingManual] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  // Check if user has QuickBooks configured
  const hasQuickBooksConfigured = useMemo(() => {
    return (
      user?.settings?.qbBankAccount &&
      user?.settings?.qbIncomeAccount &&
      user?.settings?.qbTaxGroup
    );
  }, [user?.settings]);

  // Debounced QuickBooks invoice lookup: after the user stops typing an
  // invoice ID, fetch it so we can show the invoice balance before charging.
  const qbLookupRequestRef = useRef("");
  useEffect(() => {
    const trimmedId = qbInvoiceId.trim();
    qbLookupRequestRef.current = trimmedId;

    if (!trimmedId) {
      setQbInvoiceLookup({ status: "idle" });
      setLinkedMmJob(null);
      return;
    }

    setQbInvoiceLookup({ status: "loading" });
    setLinkedMmJob(null);
    const timeout = setTimeout(async () => {
      try {
        const response = await authenticatedFetch(
          `/api/quickbooks/job/get?invoiceId=${encodeURIComponent(trimmedId)}`,
          { method: "GET" }
        );
        // Ignore stale responses — the user has typed something else since.
        if (qbLookupRequestRef.current !== trimmedId) return;

        if (response.ok) {
          const { data: invoice } = await readJson(response);
          if (qbLookupRequestRef.current !== trimmedId) return;

          // Before reporting "found" (which enables the Charge button),
          // check whether the invoice belongs to a MountMonitor job — those
          // must be paid via the On Job tab so MM updates too.
          let mmJob = null;
          if (invoice?.Id && user?.uid) {
            mmJob = await getJobByQbInvoiceId(user.uid, String(invoice.Id));
            if (qbLookupRequestRef.current !== trimmedId) return;
          }

          setLinkedMmJob(mmJob ? { jobId: mmJob.jobId, uid: mmJob.uid } : null);
          setQbInvoiceLookup(
            invoice ? { status: "found", invoice } : { status: "notFound" }
          );
        } else {
          setQbInvoiceLookup({ status: "notFound" });
        }
      } catch {
        if (qbLookupRequestRef.current === trimmedId) {
          setQbInvoiceLookup({ status: "notFound" });
        }
      }
    }, 600);

    return () => clearTimeout(timeout);
  }, [qbInvoiceId, user?.uid]);

  // Processing fee calculations
  const terminalProcessingFee = useMemo(() => {
    return (
      user?.settings?.processingFees?.find(
        (fee) => fee.applicableTo?.terminal === true
      ) || user?.settings?.processingFees?.[0]
    );
  }, [user?.settings?.processingFees]);

  const portalProcessingFee = useMemo(() => {
    return user?.settings?.processingFees?.find(
      (fee) => fee.applicableTo?.portal === true
    );
  }, [user?.settings?.processingFees]);

  const processingFeeOptions = useMemo(() => {
    const isManualEntry = activeTab === "manual-entry";

    return (
      user?.settings?.processingFees
        ?.filter((fee) => {
          if (isManualEntry) {
            // Manual entry: only show fees applicable to portal
            return fee.applicableTo?.portal === true;
          } else {
            // Card reader: only show fees applicable to terminal
            return fee.applicableTo?.terminal === true;
          }
        })
        .map((fee) => ({
          value: fee.name,
          label:
            fee.name && fee.name.toLowerCase() !== "processing fee"
              ? fee.name.split("Processing Fee - ")[1] || fee.name
              : fee.name || "Unknown Fee",
        })) || []
    );
  }, [user?.settings?.processingFees, activeTab]);

  const selectedFeeDetails = useMemo(() => {
    if (!selectedProcessingFee) return null;
    return user?.settings?.processingFees?.find(
      (fee) => fee.name === selectedProcessingFee
    );
  }, [selectedProcessingFee, user?.settings?.processingFees]);

  const calculatedProcessingFee = useMemo(() => {
    if (isRecordOnly || !selectedFeeDetails || oneOffAmount <= 0) return 0;
    return calculateJob
      .processingFeeForPayment(
        oneOffAmount,
        selectedFeeDetails.rate || 0,
        selectedFeeDetails.fixed || 0
      )
      .raw();
  }, [isRecordOnly, selectedFeeDetails, oneOffAmount]);

  const totalCharge = useMemo(() => {
    return calculateAddition(oneOffAmount, calculatedProcessingFee);
  }, [oneOffAmount, calculatedProcessingFee]);

  // Tax-inclusive extraction, same formula the payments report uses for
  // job payments: amount × (rate ÷ (100 + rate)).
  const oneOffTaxPortion = useMemo(() => {
    if (!oneOffTaxIncluded || userTaxRate <= 0 || oneOffAmount <= 0) return 0;
    return toDecimal(oneOffAmount)
      .times(toDecimal(userTaxRate))
      .dividedBy(toDecimal(100).plus(toDecimal(userTaxRate)))
      .toDecimalPlaces(2)
      .toNumber();
  }, [oneOffTaxIncluded, userTaxRate, oneOffAmount]);

  // Record the charge for the payments report as soon as it succeeds. The
  // Stripe webhook and the report's reconcile write the same doc id as
  // backstops if this write is interrupted, so failures here are logged and
  // never surfaced to the user.
  const buildOneOffPayment = (
    uid: string,
    method: OneOffPayment["method"],
    stripePaymentIntentId: string | null
  ): OneOffPayment => ({
    uid,
    stripePaymentIntentId,
    amount: oneOffAmount,
    processingFee: calculatedProcessingFee,
    total: totalCharge,
    currency: (user?.settings?.currency || "USD").toUpperCase(),
    description: oneOffDescription,
    method,
    taxIncluded: oneOffTaxIncluded && userTaxRate > 0,
    taxRate: oneOffTaxIncluded && userTaxRate > 0 ? userTaxRate : 0,
    date: Timestamp.now(),
    createdAt: Timestamp.now(),
    source: "client",
  });

  const recordOneOffPayment = async (
    paymentIntentId: string,
    method: "terminal" | "manual"
  ) => {
    try {
      await createOneOffPayment(
        user.uid,
        buildOneOffPayment(paymentIntentId, method, paymentIntentId)
      );
    } catch (error) {
      console.error("Failed to record one-off payment:", error);
    }
  };

  // When a QB invoice is linked, cap the amount at the invoice's remaining
  // balance so we never leave an unapplied credit in QuickBooks. The
  // processing fee is excluded — it gets added onto the invoice itself.
  const qbInvoiceBalance =
    qbInvoiceLookup.status === "found"
      ? (qbInvoiceLookup.invoice.Balance ?? 0)
      : null;
  const exceedsQbBalance =
    qbInvoiceBalance !== null && isGreaterThan(oneOffAmount, qbInvoiceBalance);

  // Auto-select processing fee based on tab
  useEffect(() => {
    if (user.uid === NamedUserUID.Mesquite || user.uid === NamedUserUID.ALLOVER)
      return;

    if (activeTab === "card-reader" && terminalProcessingFee) {
      setSelectedProcessingFee(terminalProcessingFee.name);
    } else if (activeTab === "manual-entry" && portalProcessingFee) {
      setSelectedProcessingFee(portalProcessingFee.name);
    }
  }, [activeTab, terminalProcessingFee, portalProcessingFee, user.uid]);

  useEffect(() => {
    // Restore the saved spot (clamped in case the window is smaller now);
    // fall back to the bottom-right default.
    let restored = {
      x: window.innerWidth - 80,
      y: window.innerHeight - 120,
    };
    try {
      const raw = localStorage.getItem(POSITION_KEY);
      if (raw) restored = JSON.parse(raw);
    } catch {
      // ignore malformed storage
    }
    setPosition(clampToViewport(restored));
  }, []);

  // Keep it on-screen when the window shrinks.
  useEffect(() => {
    const onResize = () => setPosition((pos) => clampToViewport(pos));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      hasMovedRef.current = true;
      setPosition({
        x: e.clientX - offsetRef.current.x,
        y: e.clientY - offsetRef.current.y,
      });
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDraggingRef.current) return;
      hasMovedRef.current = true;
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - offsetRef.current.x,
        y: touch.clientY - offsetRef.current.y,
      });
    };

    const persistPosition = () => {
      if (!isDraggingRef.current) return;
      try {
        localStorage.setItem(POSITION_KEY, JSON.stringify(positionRef.current));
      } catch {
        // ignore storage failures
      }
    };

    const handleMouseUp = () => {
      persistPosition();
      setIsDragging(false);
      setTimeout(() => {
        isDraggingRef.current = false;
        hasMovedRef.current = false;
      }, 0);
    };

    const handleTouchEnd = () => {
      persistPosition();
      setIsDragging(false);
      setTimeout(() => {
        isDraggingRef.current = false;
        hasMovedRef.current = false;
      }, 0);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = dragRef.current?.getBoundingClientRect();
    if (rect) {
      offsetRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }

    const startX = e.clientX;
    const startY = e.clientY;
    const moveThreshold = 5;

    const checkForDrag = (moveEvent: MouseEvent) => {
      const deltaX = Math.abs(moveEvent.clientX - startX);
      const deltaY = Math.abs(moveEvent.clientY - startY);

      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        isDraggingRef.current = true;
        setIsDragging(true);
        document.removeEventListener("mousemove", checkForDrag);
      }
    };

    document.addEventListener("mousemove", checkForDrag);

    const cleanup = () => {
      document.removeEventListener("mousemove", checkForDrag);
      document.removeEventListener("mouseup", cleanup);
    };

    document.addEventListener("mouseup", cleanup);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const rect = dragRef.current?.getBoundingClientRect();
    const touch = e.touches[0];
    if (rect) {
      offsetRef.current = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      };
    }

    const startX = touch.clientX;
    const startY = touch.clientY;
    const moveThreshold = 5;

    const checkForDrag = (moveEvent: TouchEvent) => {
      const moveTouch = moveEvent.touches[0];
      const deltaX = Math.abs(moveTouch.clientX - startX);
      const deltaY = Math.abs(moveTouch.clientY - startY);

      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        isDraggingRef.current = true;
        setIsDragging(true);
        document.removeEventListener("touchmove", checkForDrag);
      }
    };

    document.addEventListener("touchmove", checkForDrag);

    const cleanup = () => {
      document.removeEventListener("touchmove", checkForDrag);
      document.removeEventListener("touchend", cleanup);
    };

    document.addEventListener("touchend", cleanup);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (hasMovedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    open();
  };

  const handleManualPayment = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (oneOffAmount < 1) {
      addAlert({
        type: "error",
        title: "Invalid Amount",
        content: "Amount must be at least $1.00",
      });
      return;
    }

    if (exceedsQbBalance) {
      addAlert({
        type: "error",
        title: "Invalid Amount",
        content: "Amount should be equal to or less than the remaining balance",
      });
      return;
    }

    if (linkedMmJob !== null) {
      addAlert({
        type: "error",
        title: "Invoice Belongs to a MountMonitor Job",
        content: `This invoice belongs to Job #${linkedMmJob.jobId}. Take this payment on the On Job tab so the job is updated too.`,
      });
      return;
    }

    if (!oneOffDescription.trim()) {
      addAlert({
        type: "error",
        title: "Description Required",
        content: "Please enter a description for this charge",
      });
      return;
    }

    setIsProcessingManual(true);

    try {
      // Create payment intent
      const response = await authenticatedFetch(
        "/api/stripe/manual-payment-intent",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: user?.uid,
            amount: oneOffAmount,
            description: oneOffDescription,
            processingFee: calculatedProcessingFee,
            stripeAccountId: user?.settings?.stripeAccountId,
            currency: user?.settings?.currency || "USD",
            qbInvoiceId: resolvedQbInvoiceId || null,
            taxIncluded: oneOffTaxIncluded && userTaxRate > 0,
            taxRate: userTaxRate,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await readJson(response);
        throw new Error(errorData.error || "Failed to create payment intent");
      }

      const { clientSecret, paymentIntentId } = await readJson(response);

      // Confirm the payment
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (error) {
        throw new Error(error.message || "Payment failed");
      }

      if (paymentIntent) {
        await recordOneOffPayment(paymentIntent.id, "manual");
      }

      // Handle QuickBooks integration if needed
      if (qbInvoiceId && user?.settings?.qbBankAccount && paymentIntent) {
        try {
          const invoiceResponse = await authenticatedFetch(
            `/api/quickbooks/job/get?invoiceId=${resolvedQbInvoiceId}&by=id`,
            {
              method: "GET",
            }
          );

          if (invoiceResponse.ok) {
            const { data: invoice } = await readJson(invoiceResponse);
            if (invoice && invoice.CustomerRef) {
              const mockClient: Partial<Client> = {
                quickbooks: {
                  id: invoice.CustomerRef.value,
                },
                currency: {
                  code: user?.settings?.currency.toUpperCase(),
                },
              } as any;

              const jobPayment: JobPayment = {
                uid: `manual_${Date.now()}`,
                amount: oneOffAmount,
                processingFee: calculatedProcessingFee,
                tax: null,
                total: totalCharge,
                type: PaymentType.Partial,
                date: Timestamp.now(),
                notes: `${oneOffDescription}`,
                stripePaymentIntentId: paymentIntent.id,
              };

              const paymentCreateResponse = await authenticatedFetch(
                `/api/quickbooks/payments/create`,
                {
                  method: "POST",
                  body: JSON.stringify({
                    jobId: "one-off-charge",
                    client: mockClient,
                    jobPayment: jobPayment,
                    qbInvoiceId: resolvedQbInvoiceId,
                    qbBankAccount: user?.settings?.qbBankAccount,
                    processingFee:
                      calculatedProcessingFee > 0
                        ? {
                            amount: calculatedProcessingFee,
                            itemName: "Processing Fee for One-off Payment",
                            incomeAccountId:
                              user?.settings?.qbIncomeAccount?.id || null,
                          }
                        : null,
                  }),
                }
              );

              if (paymentCreateResponse.ok) {
                const { warning } = await readJson(paymentCreateResponse);
                if (warning) {
                  addAlert({
                    duration: 10000,
                    type: "warning",
                    title: "QuickBooks Fee Line",
                    content: warning,
                  });
                }
              }
            }
          }
        } catch (qbError) {
          console.error("QuickBooks integration error:", qbError);
          addAlert({
            duration: 10000,
            type: "error",
            title: "QuickBooks Integration Failed",
            content:
              "Payment was collected but QuickBooks sync failed. Please manually record the payment.",
          });
        }
      }

      addAlert({
        type: "success",
        title: "Payment Successful!",
        content: `Successfully charged ${formatCurrency(
          totalCharge,
          userCurrency
        )} for "${oneOffDescription}"${
          qbInvoiceLookup.status === "found"
            ? ` on Invoice #${
                qbInvoiceLookup.invoice.DocNumber || resolvedQbInvoiceId
              }`
            : ""
        }`,
      });

      // Reset form
      setOneOffAmount(1);
      setOneOffDescription("");
      setQbInvoiceId("");
      cardElement.clear();
      close();
    } catch (err: any) {
      console.error("Manual payment failed:", err);
      addAlert({
        type: "error",
        title: "Payment Failed",
        content: err.message || "Failed to process payment",
      });
    } finally {
      setIsProcessingManual(false);
    }
  };

  // Recorded (non-card) payments: nothing to charge, so the oneOffPayments
  // doc is the only record. Unlike card charges (where Stripe is the
  // backstop) a failed write here is a failed payment and is surfaced as one.
  const handleRecordOneOff = async () => {
    const tender = recordTender;

    if (!(oneOffAmount > 0)) {
      addAlert({
        type: "error",
        title: "Invalid Amount",
        content: "Amount must be greater than zero",
      });
      return;
    }

    if (exceedsQbBalance) {
      addAlert({
        type: "error",
        title: "Invalid Amount",
        content: "Amount should be equal to or less than the remaining balance",
      });
      return;
    }

    if (linkedMmJob !== null) {
      addAlert({
        type: "error",
        title: "Invoice Belongs to a MountMonitor Job",
        content: `This invoice belongs to Job #${linkedMmJob.jobId}. Take this payment on the On Job tab so the job is updated too.`,
      });
      return;
    }

    if (!oneOffDescription.trim()) {
      addAlert({
        type: "error",
        title: "Description Required",
        content: "Please enter a description for this payment",
      });
      return;
    }

    setIsRecordingOneOff(true);

    try {
      const paymentUid = `recorded_${Date.now()}`;
      await createOneOffPayment(user.uid, {
        ...buildOneOffPayment(paymentUid, "recorded", null),
        tender,
      });

      if (qbInvoiceId && user?.settings?.qbBankAccount) {
        try {
          const invoiceResponse = await authenticatedFetch(
            `/api/quickbooks/job/get?invoiceId=${resolvedQbInvoiceId}&by=id`,
            { method: "GET" }
          );
          const { data: invoice } = invoiceResponse.ok
            ? await readJson(invoiceResponse)
            : { data: null };
          if (!invoice?.CustomerRef) {
            throw new Error("Invoice not found or missing customer reference");
          }

          const mockClient: Partial<Client> = {
            quickbooks: {
              id: invoice.CustomerRef.value,
            },
            currency: {
              code: user?.settings?.currency.toUpperCase(),
            },
          } as any;

          const jobPayment: JobPayment = {
            uid: paymentUid,
            amount: oneOffAmount,
            processingFee: 0,
            tax: null,
            total: totalCharge,
            type: PaymentType.Partial,
            date: Timestamp.now(),
            notes: `${oneOffDescription}`,
            tender,
          };

          const paymentResponse = await authenticatedFetch(
            `/api/quickbooks/payments/create`,
            {
              method: "POST",
              body: JSON.stringify({
                jobId: "one-off-charge",
                client: mockClient,
                jobPayment,
                qbInvoiceId: resolvedQbInvoiceId,
                qbBankAccount: user?.settings?.qbBankAccount,
                processingFee: null,
              }),
            }
          );
          if (!paymentResponse.ok) {
            throw new Error("Failed to create QuickBooks payment");
          }
        } catch (qbError) {
          console.error("QuickBooks integration error:", qbError);
          addAlert({
            duration: 10000,
            type: "error",
            title: "QuickBooks Integration Failed",
            content:
              "The payment was recorded but QuickBooks sync failed. Please manually record the payment.",
          });
        }
      }

      addAlert({
        type: "success",
        title: "Payment Recorded",
        content: `Recorded ${formatCurrency(
          totalCharge,
          userCurrency
        )} ${tender.toLowerCase()} for "${oneOffDescription}"${
          qbInvoiceLookup.status === "found"
            ? ` on Invoice #${
                qbInvoiceLookup.invoice.DocNumber || resolvedQbInvoiceId
              }`
            : ""
        }`,
      });

      setOneOffAmount(1);
      setOneOffDescription("");
      setQbInvoiceId("");
      close();
    } catch (err: any) {
      console.error("Recording one-off payment failed:", err);
      addAlert({
        type: "error",
        title: "Payment Not Recorded",
        content: err.message || "Failed to record payment",
      });
    } finally {
      setIsRecordingOneOff(false);
    }
  };

  const handleOneOffCharge = async () => {
    if (oneOffAmount < 1) {
      addAlert({
        type: "error",
        title: "Invalid Amount",
        content: "Amount must be at least $1.00",
      });
      return;
    }

    if (exceedsQbBalance) {
      addAlert({
        type: "error",
        title: "Invalid Amount",
        content: "Amount should be equal to or less than the remaining balance",
      });
      return;
    }

    if (linkedMmJob !== null) {
      addAlert({
        type: "error",
        title: "Invoice Belongs to a MountMonitor Job",
        content: `This invoice belongs to Job #${linkedMmJob.jobId}. Take this payment on the On Job tab so the job is updated too.`,
      });
      return;
    }

    if (!oneOffDescription.trim()) {
      addAlert({
        type: "error",
        title: "Description Required",
        content: "Please enter a description for this charge",
      });
      return;
    }

    setReaderAlert(null);
    // A charge always closes the picker first, so its outcome is never hidden
    // behind the reader list.
    setShowReaderPicker(false);
    setIsProcessingOneOff(true);

    try {
      // Take the reader, create the intent, collect, process, let the reader
      // go. A busy or missing reader comes back as run.ok === false before
      // any intent exists, so nothing is left orphaned in Stripe.
      const run = await runWithReader(async ({ collectAndProcess }) => {
        // Call one-off charge endpoint
        const response = await authenticatedFetch(
          "/api/stripe/terminal/one-off-charge",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              userId: user?.uid,
              amount: oneOffAmount,
              description: oneOffDescription,
              processingFee: calculatedProcessingFee,
              stripeAccountId: user?.settings?.stripeAccountId,
              currency: user?.settings?.currency || "USD",
              qbInvoiceId: resolvedQbInvoiceId || null,
              qbBankAccount: user?.settings?.qbBankAccount || null,
              taxIncluded: oneOffTaxIncluded && userTaxRate > 0,
              taxRate: userTaxRate,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await readJson(response);
          throw new Error(
            errorData.error || "Failed to create payment intent"
          );
        }

        const { clientSecret, paymentIntentId } = await readJson(response);

        // Wait for the card and charge it.
        await collectAndProcess(clientSecret);

        return { paymentIntentId: paymentIntentId as string };
      });

      if (run.ok === false) {
        setReaderAlert({
          message: run.error,
          readerFailure: run.readerFailure,
        });
        // The saved reader is gone or offline: show the others to pick from.
        if (
          hasMultipleReaders &&
          (run.readerFailure === "not_found" ||
            run.readerFailure === "offline")
        ) {
          setShowReaderPicker(true);
        }
        return;
      }

      const { paymentIntentId } = run.value;

      await recordOneOffPayment(paymentIntentId, "terminal");

      // Initialize QuickBooks details response
      let quickbooksDetails: any = null;

      // Handle QuickBooks integration if invoice ID is provided
      if (qbInvoiceId && user?.settings?.qbBankAccount) {
        try {
          // Step 1: Fetch the invoice using the existing endpoint
          const invoiceResponse = await authenticatedFetch(
            `/api/quickbooks/job/get?invoiceId=${resolvedQbInvoiceId}&by=id`,
            {
              method: "GET",
            }
          );

          if (!invoiceResponse.ok) {
            const errorData = await readJson(invoiceResponse);
            if (errorData.error === "reauthenticate") {
              quickbooksDetails = {
                success: false,
                error: "QuickBooks authentication required",
              };
            } else {
              quickbooksDetails = {
                success: false,
                error: (
                  <Text>
                    Could not find matching invoice in Quickbooks for
                    <Anchor
                      href={`https://qbo.intuit.com/app/invoice?txnId=${resolvedQbInvoiceId}`}
                      target="_blank"
                      style={{
                        color: "inherit",
                        fontSize: "inherit",
                        textDecoration: "underline",
                      }}
                      ml="2"
                    >
                      #{qbInvoiceId}
                    </Anchor>
                  </Text>
                ),
              };
            }
          } else {
            const { data: invoice } = await readJson(invoiceResponse);

            if (invoice && invoice.CustomerRef) {
              // Step 2: Create a mock client object with QuickBooks info
              const mockClient: Partial<Client> = {
                quickbooks: {
                  id: invoice.CustomerRef.value,
                },
                currency: {
                  code: user?.settings?.currency.toUpperCase(),
                },
              } as any;

              // Step 3: Create a payment object
              const jobPayment: JobPayment = {
                uid: `terminal_${Date.now()}`,
                amount: oneOffAmount,
                processingFee: calculatedProcessingFee,
                tax: null,
                total: totalCharge,
                type: PaymentType.Partial,
                date: Timestamp.now(),
                notes: `${oneOffDescription}`,
                stripePaymentIntentId: paymentIntentId,
              };

              // Step 4: Create payment in QuickBooks using the existing endpoint
              const paymentResponse = await authenticatedFetch(
                `/api/quickbooks/payments/create`,
                {
                  method: "POST",

                  body: JSON.stringify({
                    jobId: "one-off-charge", // Pseudo job ID for terminal payments
                    client: mockClient,
                    jobPayment: jobPayment,
                    qbInvoiceId: resolvedQbInvoiceId,
                    qbBankAccount: user?.settings?.qbBankAccount,
                    processingFee:
                      calculatedProcessingFee > 0
                        ? {
                            amount: calculatedProcessingFee,
                            itemName: "Processing Fee for One-off Payment",
                            incomeAccountId:
                              user?.settings?.qbIncomeAccount?.id || null,
                          }
                        : null,
                  }),
                }
              );

              if (!paymentResponse.ok) {
                const errorData = await readJson(paymentResponse);
                quickbooksDetails = {
                  success: false,
                  error: "Failed to create QuickBooks payment",
                  details: errorData.details,
                };
              } else {
                const { data: payment, warning } =
                  await readJson(paymentResponse);
                console.log({ payment: payment });
                quickbooksDetails = {
                  success: true,
                  payment: payment,
                  invoiceId: resolvedQbInvoiceId,
                };
                if (warning) {
                  addAlert({
                    duration: 10000,
                    type: "warning",
                    title: "QuickBooks Fee Line",
                    content: warning,
                  });
                }
              }
            } else {
              quickbooksDetails = {
                success: false,
                error: "Invoice not found or missing customer reference",
              };
            }
          }
        } catch (qbError: any) {
          console.error("QuickBooks integration error:", qbError);
          quickbooksDetails = {
            success: false,
            error: "QuickBooks integration failed",
          };
        }
      }

      addAlert({
        type: "success",
        title: "Payment Successful!",
        content: `Successfully charged ${formatCurrency(
          totalCharge,
          userCurrency
        )} for "${oneOffDescription}"${
          qbInvoiceLookup.status === "found"
            ? ` on Invoice #${
                qbInvoiceLookup.invoice.DocNumber || resolvedQbInvoiceId
              }`
            : ""
        }`,
      });

      if (quickbooksDetails?.success === false) {
        addAlert({
          duration: 10000,
          type: "error",
          title: "The payment was collected however QuickBooks failed.",
          content: quickbooksDetails.error,
        });
      }

      // Reset form
      setOneOffAmount(1);
      setOneOffDescription("");
      setQbInvoiceId("");
      close();
    } catch (err: any) {
      console.error("One-off payment failed:", err);
      // Same copy as the job payment form: declines name the reason, reader
      // failures get fixed copy. A cancelled card read needs no message.
      const described = describeTerminalChargeError(err);
      if (!described.cancelled) {
        setReaderAlert({
          message: described.message,
          cardDeclined: described.cardDeclined,
        });
      }
    } finally {
      setIsProcessingOneOff(false);
    }
  };

  return (
    <>
      <Portal>
        <div
          ref={dragRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          style={{
            position: "fixed",
            left: position.x,
            top: position.y,
            zIndex: 1000,
            cursor: isDragging ? "grabbing" : "grab",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "fit-content",
              height: "fit-content",
            }}
          >
            {isDragging && (
              <div
                style={{
                  position: "absolute",
                  inset: -8,
                  border: "2px solid var(--mantine-color-green-6)",
                  borderRadius: "50%",
                  opacity: 0.5,
                  pointerEvents: "none",
                }}
              />
            )}
            <ActionIcon
              size="xl"
              radius="xl"
              variant="filled"
              color="green"
              onClick={handleClick}
              style={{
                cursor: isDragging ? "grabbing" : "pointer",
                boxShadow:
                  "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px;",
              }}
            >
              <IconCurrencyDollar size={20} />
            </ActionIcon>
          </div>
        </div>
      </Portal>

      {/* PaymentModal: cannot be dismissed while a payment on either tab is
          under way or being saved. */}
      <PaymentModal
        withinPortal={true}
        opened={opened}
        onClose={close}
        title={
          <Title order={4} mb="md">
            Take Payment
          </Title>
        }
        size="lg"
        centered
        zIndex={10000000000000}
        // Bounded flex column so only the payment form scrolls (one scroll,
        // Cancel/Process pinned), same as the jobs board Take Payment modal.
        styles={{
          content: { display: "flex", flexDirection: "column" },
          body: {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          },
        }}
      >
        <Tabs
          value={posMode}
          onChange={setPosMode}
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Tabs.List grow mb="md">
            <Tabs.Tab value="job">On Job</Tabs.Tab>
            <Tabs.Tab value="one-off">One-off</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel
            value="job"
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <OnJobPaymentTab
              onDone={close}
              selectedJobUid={onJobSelectedUid}
              onSelectJob={setOnJobSelectedUid}
            />
          </Tabs.Panel>

          <Tabs.Panel
            value="one-off"
            style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
          >
            {/* One-off Charge Section — single form; the payment method (reader
            vs manual card entry) is a nested tab box like the job Take
            Payment modal. */}
            <Stack>
              <NumberInput
                label="Amount"
                description={isRecordOnly ? undefined : "Minimum $1.00"}
                placeholder="Enter amount"
                leftSection="$"
                min={isRecordOnly ? 0 : 1}
                decimalScale={2}
                value={oneOffAmount}
                onChange={(value) => setOneOffAmount(value as number)}
                size="md"
                required
                error={
                  exceedsQbBalance
                    ? "Amount should be equal to or less than the remaining balance"
                    : undefined
                }
              />

              <TextInput
                label="Description"
                placeholder="Enter charge description"
                value={oneOffDescription}
                onChange={(e) => setOneOffDescription(e.currentTarget.value)}
                size="md"
                required
              />

              {userTaxRate > 0 && (
                <Switch
                  label={`Amount includes sales tax (${userTaxRate}%)`}
                  description="Records the tax portion of this charge so it shows in the payments report. The amount charged is not changed."
                  checked={oneOffTaxIncluded}
                  onChange={(e) =>
                    setOneOffTaxIncluded(e.currentTarget.checked)
                  }
                />
              )}

              {hasQuickBooksConfigured && (
                <div>
                  <TextInput
                    label="QuickBooks Invoice ID"
                    placeholder="Enter invoice # (optional)"
                    value={qbInvoiceId}
                    onChange={(e) => setQbInvoiceId(e.currentTarget.value)}
                    size="md"
                    description="If provided, a payment will be created against that invoice in QuickBooks"
                    rightSection={
                      qbInvoiceLookup.status === "loading" ? (
                        <Loader size="xs" />
                      ) : undefined
                    }
                    error={
                      qbInvoiceLookup.status === "notFound"
                        ? "Invoice not found in QuickBooks"
                        : undefined
                    }
                  />
                  {qbInvoiceLookup.status === "found" &&
                    (linkedMmJob !== null ? (
                      <Alert color="red" variant="light" mt="xs" p="xs">
                        <Text size="sm">
                          This invoice belongs to{" "}
                          <b>Job #{linkedMmJob.jobId}</b> in MountMonitor. Take
                          this payment on the{" "}
                          <Anchor
                            component="button"
                            type="button"
                            size="sm"
                            fw={600}
                            onClick={() => {
                              setOnJobSelectedUid(linkedMmJob.uid);
                              setPosMode("job");
                            }}
                          >
                            On Job
                          </Anchor>{" "}
                          tab so the job is updated too.
                        </Text>
                      </Alert>
                    ) : (
                      <Alert color="green" variant="light" mt="xs" p="xs">
                        <Group justify="space-between">
                          <Text size="sm">
                            Invoice #
                            {qbInvoiceLookup.invoice.DocNumber || qbInvoiceId}
                            {qbInvoiceLookup.invoice.CustomerRef?.name
                              ? ` · ${qbInvoiceLookup.invoice.CustomerRef.name}`
                              : ""}
                          </Text>
                          <Text size="sm" fw={600}>
                            Remaining balance:{" "}
                            {formatCurrency(
                              qbInvoiceLookup.invoice.Balance ?? 0,
                              userCurrency
                            )}
                          </Text>
                        </Group>
                      </Alert>
                    ))}
                </div>
              )}

              <Paper withBorder p="sm" radius="md">
                <Tabs
                  value={activeTab}
                  onChange={(value) => {
                    userPickedTab.current = true;
                    setActiveTab(value);
                  }}
                >
                  <Tabs.List>
                    <Tabs.Tab value="card-reader" disabled={!hasAnyReader}>
                      Card Reader
                    </Tabs.Tab>
                    <Tabs.Tab value="manual-entry">Manual Entry</Tabs.Tab>
                    <Tabs.Tab value="record">Cash / Other</Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value="card-reader" pt="md">
                    <Stack gap="sm">
                      {pickerVisible ? (
                        // More than one reader and either nothing resolves
                        // for this device or Change reader was pressed. A
                        // gone or offline saved reader explains itself in
                        // the notice line.
                        <ReaderPicker
                          notice={
                            readerAlert?.readerFailure === "not_found" ||
                            readerAlert?.readerFailure === "offline"
                              ? readerAlert.message
                              : null
                          }
                          onPick={() => {
                            setShowReaderPicker(false);
                            setReaderAlert(null);
                          }}
                        />
                      ) : isTerminalReady ? (
                        <ReaderSummary
                          disabled={isReaderProcessing}
                          onChange={() => {
                            setShowReaderPicker(true);
                            setReaderAlert(null);
                          }}
                        />
                      ) : (
                        <Alert color="red" variant="light">
                          <Text size="sm">
                            {readerFailureMessage(
                              hasAnyReader ? "not_initialized" : "no_reader"
                            )}
                          </Text>
                        </Alert>
                      )}

                      {readerAlert && !pickerVisible && (
                        readerAlert.readerFailure ? (
                          <ReaderFailureNotice
                            failure={readerAlert.readerFailure}
                            message={readerAlert.message}
                          />
                        ) : (
                          <PaymentNotice
                            title={
                              readerAlert.cardDeclined
                                ? "Card declined"
                                : "Payment failed"
                            }
                            message={readerAlert.message}
                            onClose={() => setReaderAlert(null)}
                          />
                        )
                      )}
                    </Stack>
                  </Tabs.Panel>

                  <Tabs.Panel value="manual-entry" pt="md">
                    <div>
                      <Text size="sm" fw={500} mb="xs">
                        Card Details
                      </Text>
                      <div
                        style={{
                          padding: "10px",
                          border: "1px solid var(--mantine-color-gray-4)",
                          borderRadius: "4px",
                        }}
                      >
                        <CardElement
                          onChange={(event) => {
                            setCardComplete(event.complete);
                            setCardError(
                              event.error ? event.error.message : null
                            );
                          }}
                          options={{
                            hidePostalCode: true,
                            disableLink: true,
                            style: {
                              base: {
                                fontSize: "14px",
                                color: "var(--mantine-color-text)",
                                "::placeholder": {
                                  color: "var(--mantine-color-placeholder)",
                                },
                              },
                            },
                          }}
                        />
                      </div>
                      {cardError && (
                        <Text size="xs" c="red">
                          {cardError}
                        </Text>
                      )}
                    </div>
                  </Tabs.Panel>

                  <Tabs.Panel value="record" pt="md">
                    <Stack gap="xs">
                      <Select
                        label="Payment Method"
                        data={RECORD_TENDERS}
                        value={recordTender}
                        onChange={(value) => setRecordTender(value || "Cash")}
                        allowDeselect={false}
                        checkIconPosition="right"
                        size="md"
                        comboboxProps={{ zIndex: 20000000000 }}
                      />
                      <Text size="sm" c="dimmed">
                        Records the payment. Nothing is charged.
                      </Text>
                    </Stack>
                  </Tabs.Panel>
                </Tabs>
              </Paper>

              {!isRecordOnly && (
                <Select
                  label="Processing Fee"
                  placeholder="Select processing fee"
                  data={processingFeeOptions}
                  value={selectedProcessingFee}
                  checkIconPosition="right"
                  clearable
                  size="md"
                  onChange={(value) => setSelectedProcessingFee(value)}
                  comboboxProps={{ zIndex: 20000000000 }}
                />
              )}

              {oneOffAmount > 0 && (
                <Alert variant="light" p="sm">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm">Amount:</Text>
                      <Text size="sm" fw={500}>
                        {formatCurrency(oneOffAmount, userCurrency)}
                      </Text>
                    </Group>
                    {calculatedProcessingFee > 0 && (
                      <>
                        <Group justify="space-between">
                          <Text size="sm">Processing Fee:</Text>
                          <Text size="sm" fw={500}>
                            {formatCurrency(
                              calculatedProcessingFee,
                              userCurrency
                            )}
                          </Text>
                        </Group>
                        <Divider />
                      </>
                    )}
                    <Group justify="space-between">
                      <Text fw={600}>
                        {isRecordOnly ? "Total:" : "Total Charge:"}
                      </Text>
                      <Text fw={600}>
                        {formatCurrency(totalCharge, userCurrency)}
                      </Text>
                    </Group>
                    {oneOffTaxPortion > 0 && (
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed">
                          Includes sales tax of:
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatCurrency(oneOffTaxPortion, userCurrency)}
                        </Text>
                      </Group>
                    )}
                  </Stack>
                </Alert>
              )}

              {activeTab === "card-reader" && isReaderProcessing && (
                // Shown for the whole charge so nothing jumps; live while the
                // reader is being taken or waits for the card.
                <Group justify="flex-end">
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    disabled={!canCancel}
                    onClick={cancelTerminalPayment}
                  >
                    Cancel
                  </Button>
                </Group>
              )}

              <Button
                onClick={() =>
                  isRecordOnly
                    ? handleRecordOneOff()
                    : activeTab === "card-reader"
                      ? handleOneOffCharge()
                      : handleManualPayment()
                }
                loading={
                  isRecordOnly
                    ? isRecordingOneOff
                    : activeTab === "card-reader"
                      ? isProcessingOneOff || isReaderProcessing
                      : isProcessingManual
                }
                disabled={
                  (isRecordOnly ? !(oneOffAmount > 0) : oneOffAmount < 1) ||
                  !oneOffDescription ||
                  exceedsQbBalance ||
                  linkedMmJob !== null ||
                  // Wait for the invoice lookup — it may reveal an MM-owned
                  // invoice or an over-balance amount that must block the charge.
                  qbInvoiceLookup.status === "loading" ||
                  (isRecordOnly
                    ? isRecordingOneOff
                    : activeTab === "card-reader"
                      ? !isTerminalReady ||
                        pickerVisible ||
                        // A payment on the On Job tab still has the reader.
                        readerLocked ||
                        isProcessingOneOff ||
                        isReaderProcessing
                      : !stripe ||
                        isProcessingManual ||
                        !cardComplete ||
                        !!cardError)
                }
                leftSection={
                  isRecordOnly ? (
                    <IconCash size={16} />
                  ) : (
                    <IconCreditCard size={16} />
                  )
                }
              >
                {isRecordOnly ? "Record" : "Charge"}{" "}
                {formatCurrency(totalCharge, userCurrency)}
              </Button>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </PaymentModal>
    </>
  );
}

interface OnJobPaymentTabProps {
  onDone: () => void;
  selectedJobUid: string | null;
  onSelectJob: (uid: string | null) => void;
}

/**
 * "On Job" tab of the POS modal: search for a job with an outstanding
 * balance, then take a payment against it using the exact same flow as the
 * jobs board Take Payment modal (TakeJobPaymentForm).
 */
function OnJobPaymentTab({
  onDone,
  selectedJobUid,
  onSelectJob,
}: OnJobPaymentTabProps) {
  const user = useUserContext();
  const readerLocked = useReaderLocked();
  const formatCurrency = useFormatCurrency();
  // Index mode: option labels come from the slim jobsIndex docs, and the
  // FULL job is fetched on select before it reaches TakeJobPaymentForm
  // (which spreads + persists its job — a slim entry must never reach it).
  const useIndex = isJobsIndexEnabledFor(user.uid);
  const { data: legacyJobs, isLoading: isLegacyLoading } =
    useGetJobsWithCacheV2(!useIndex);
  const { data: indexJobs, isLoading: isIndexLoading } = useQuery({
    queryKey: jobsIndexQueryKey(user.uid),
    queryFn: () => getJobsIndex(user.uid),
    enabled: !!user.uid && useIndex,
    staleTime: 15 * 60 * 1000,
  });
  const jobs = (useIndex ? indexJobs : legacyJobs) as JobV2[] | undefined;
  const isLoading = useIndex ? isIndexLoading : isLegacyLoading;

  // Placeholder job so the payment form can render (greyed out) before a
  // job is picked. mode defaults to "traditional" so contract fields are
  // never dereferenced.
  const stubJob = useMemo(() => {
    const currency = getCurrencyDetailsByCode(
      user?.settings?.currency || "USD"
    );
    return {
      uid: "",
      jobId: 0,
      jobStatus: "active",
      client: { firstName: "", lastName: "", currency },
      lineItems: [],
      payments: [],
      files: [],
      tags: [],
      notes: "",
      paymentStatus: PaymentStatus.PartiallyPaid,
      subtotal: 0,
      totalDiscount: 0,
      subtotalAfterDiscounts: 0,
      totalTax: 0,
      grandTotal: 0,
      totalDeposit: 0,
      remainingBalance: 0,
      includeTax: false,
      taxRate: 0,
    } as unknown as JobV2;
  }, [user?.settings?.currency]);

  const eligibleJobs = useMemo(
    () =>
      (jobs || []).filter(
        (job) => job.jobStatus !== "pending" && !nearZero(job.remainingBalance)
      ),
    [jobs]
  );

  const options = useMemo(
    () =>
      eligibleJobs.map((job) => ({
        value: job.uid,
        label: `#${job.jobId} · ${[job.client.firstName, job.client.lastName]
          .filter(Boolean)
          .join(" ")} · ${formatCurrency(
          job.remainingBalance,
          job.client.currency
        )} due`,
      })),
    [eligibleJobs, formatCurrency]
  );

  const { data: fetchedSelectedJob } = useGetJobByJobUid(
    user.uid,
    useIndex && selectedJobUid ? selectedJobUid : ""
  );

  const selectedJob = useIndex
    ? fetchedSelectedJob?.uid === selectedJobUid
      ? fetchedSelectedJob
      : null
    : eligibleJobs.find((job) => job.uid === selectedJobUid) || null;

  return (
    <Stack style={{ flex: 1, minHeight: 0 }}>
      <Select
        label="Job"
        placeholder={
          isLoading ? "Loading jobs…" : "Search by job # or client name"
        }
        searchable
        clearable
        size="md"
        data={options}
        value={selectedJobUid}
        onChange={onSelectJob}
        checkIconPosition="right"
        // Locked while a payment is under way: changing the job remounts the
        // payment form, which would abandon the card read it started.
        disabled={isLoading || readerLocked}
        nothingFoundMessage="No jobs with an outstanding balance"
        comboboxProps={{ zIndex: 20000000000 }}
      />

      <Divider
        label={
          selectedJob
            ? `Take Payment for Job #${selectedJob.jobId}`
            : "Select a job to take a payment"
        }
        labelPosition="center"
      />

      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
        aria-disabled={!selectedJob}
      >
        <TakeJobPaymentForm
          key={selectedJob?.uid || "no-job"}
          job={selectedJob || stubJob}
          onDone={onDone}
          onCancel={onDone}
          fillHeight
        />
        {/* Block interaction with the placeholder form until a job is
            picked — the veil greys out only the covered area, leaving the
            footer row (Cancel) fully visible and clickable. Process is
            disabled by validation anyway. */}
        {!selectedJob && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 64,
              cursor: "not-allowed",
              backgroundColor: "var(--mantine-color-body)",
              opacity: 0.5,
            }}
          />
        )}
      </div>
    </Stack>
  );
}

export function POSTrigger() {
  const user = useUserContext();

  // Initialize Stripe with the connected account ID
  const stripePromiseWithAccount = useMemo(() => {
    if (user?.settings?.stripeAccountId) {
      return loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!, {
        stripeAccount: user.settings.stripeAccountId,
      });
    }
    return getPlatformStripePromise();
  }, [user?.settings?.stripeAccountId]);

  return (
    <Elements stripe={stripePromiseWithAccount}>
      <POSTriggerContent />
    </Elements>
  );
}
